import { CalendarClock, Phone, Clock } from 'lucide-react';
import { crmTelHref } from './crmPhone';
import {
  postCrmOrderActivity,
  patchCrmOrderTask,
  patchCrmLeadTask,
  postCrmLeadActivity,
  type CrmMyDay,
  type CrmTask,
} from '../../../api';
import { useCrmMyDay, useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { CrmInlineError } from './CrmInlineError';
import type { CrmOpenDealHandler } from './crmDealDeepLink';

type Props = {
  managerId?: number | null;
  onOpenDeal: CrmOpenDealHandler;
  hapticSelection: () => void;
};

function bumpTomorrowIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(18, 0, 0, 0);
  return d.toISOString();
}

function buildSortedTasks(data: CrmMyDay | undefined) {
  if (!data) return [];
  type Row = CrmTask & {
    order_number?: string | null;
    lead_title?: string | null;
    client_phone?: string | null;
    variant: 'overdue' | 'today';
  };
  const rows: Row[] = [
    ...data.overdue.map((t) => ({ ...t, variant: 'overdue' as const })),
    ...data.due_today.map((t) => ({ ...t, variant: 'today' as const })),
  ];
  return rows.sort((a, b) => {
    const ta = a.due_at ? new Date(a.due_at).getTime() : 0;
    const tb = b.due_at ? new Date(b.due_at).getTime() : 0;
    return ta - tb;
  });
}

export function CrmMyDayPanel({ managerId, onOpenDeal, hapticSelection }: Props) {
  const { data, isLoading, isError, error, refetch } = useCrmMyDay(managerId);
  const invalidate = useInvalidateCrm();

  const sortedTasks = buildSortedTasks(data);

  const quickCall = async (
    task: CrmTask & { order_number?: string | null; lead_title?: string | null; client_phone?: string | null },
    opts?: { logOnly?: boolean },
  ) => {
    hapticSelection();
    if (!opts?.logOnly) {
      const body = 'Звонок — отмечено из «Мой день»';
      if (task.order_id) {
        await postCrmOrderActivity(task.order_id, { body, kind: 'call' });
      } else if (task.lead_id) {
        await postCrmLeadActivity(task.lead_id, { body, kind: 'call' });
      }
      void refetch();
      void invalidate();
    }
  };

  const snooze = async (task: CrmTask) => {
    hapticSelection();
    const due = bumpTomorrowIso();
    if (task.order_id) {
      await patchCrmOrderTask(task.order_id, task.id, { due_at: due });
    } else if (task.lead_id) {
      await patchCrmLeadTask(task.lead_id, task.id, { due_at: due });
    }
    void refetch();
    void invalidate();
  };

  if (isLoading && !data) {
    return <p className="text-xs py-2" style={{ color: 'var(--text-3)' }}>Загрузка задач…</p>;
  }
  if (isError) {
    return (
      <CrmInlineError
        message={formatAdminPanelError(error, 'Не удалось загрузить «Мой день»')}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const total =
    data.due_today.length +
    data.overdue.length +
    data.orders_without_task.length +
    data.leads_without_task.length;
  if (total === 0) {
    return (
      <p className="text-xs py-3 text-center" style={{ color: 'var(--text-3)' }}>
        На сегодня задач нет
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <StatsRow data={data} />
      {sortedTasks.length > 0 && (
        <div className="space-y-1.5">
          {sortedTasks.map((row) => (
            <TaskRow
              key={`${row.variant}-${row.id}`}
              task={row}
              variant={row.variant}
              onOpen={() => {
                hapticSelection();
                if (row.order_id) onOpenDeal({ kind: 'order', id: row.order_id });
                else if (row.lead_id) onOpenDeal({ kind: 'lead', id: row.lead_id });
              }}
              onCall={() => void quickCall(row, { logOnly: Boolean(crmTelHref(row.client_phone)) })}
              onSnooze={() => void snooze(row)}
              hapticSelection={hapticSelection}
            />
          ))}
        </div>
      )}
      {(data.orders_without_task.length > 0 || data.leads_without_task.length > 0) && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wide px-0.5" style={{ color: '#F6AD55' }}>
            Нет открытой задачи — запланируйте шаг
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.orders_without_task.map((row) => (
              <button
                key={`o-${row.id}`}
                type="button"
                onClick={() => {
                  hapticSelection();
                  onOpenDeal({ kind: 'order', id: row.id }, undefined, { focusTasks: true });
                }}
                className="press text-[10px] font-bold px-2.5 py-2 rounded-lg touch-manipulation min-h-[40px]"
                style={{ background: 'rgba(246,173,85,0.15)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
              >
                {row.order_number}
              </button>
            ))}
            {data.leads_without_task.map((lead) => (
              <button
                key={`l-${lead.id}`}
                type="button"
                onClick={() => {
                  hapticSelection();
                  onOpenDeal({ kind: 'lead', id: lead.id }, undefined, { focusTasks: true });
                }}
                className="press text-[10px] font-bold px-2.5 py-2 rounded-lg touch-manipulation min-h-[40px] max-w-full truncate"
                style={{ background: 'rgba(246,173,85,0.15)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
              >
                {lead.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatsRow({ data }: { data: CrmMyDay }) {
  const chips = [
    data.overdue.length > 0 ? { t: `просрочено ${data.overdue.length}`, c: '#FC814A' } : null,
    data.due_today.length > 0 ? { t: `сегодня ${data.due_today.length}`, c: '#63B3ED' } : null,
    data.orders_without_task.length > 0 ? { t: `без задачи ${data.orders_without_task.length}`, c: '#F6AD55' } : null,
    data.leads_without_task.length > 0 ? { t: `лиды ${data.leads_without_task.length}`, c: '#68D391' } : null,
  ].filter(Boolean) as { t: string; c: string }[];

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <span key={c.t} className="text-[10px] font-bold px-2 py-1 rounded-lg" style={{ background: `${c.c}22`, color: c.c }}>
          {c.t}
        </span>
      ))}
    </div>
  );
}

function TaskRow({
  task,
  variant,
  onOpen,
  onCall,
  onSnooze,
  hapticSelection,
}: {
  task: CrmTask & {
    order_number?: string | null;
    lead_title?: string | null;
    client_phone?: string | null;
    variant: 'overdue' | 'today';
  };
  variant: 'overdue' | 'today';
  onOpen: () => void;
  onCall: () => void;
  onSnooze: () => void;
  hapticSelection: () => void;
}) {
  const sub = task.order_number || task.lead_title || '';
  const dueLabel = task.due_at
    ? new Date(task.due_at).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
  const isCall = task.kind === 'call';
  const telHref = crmTelHref(task.client_phone);

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: variant === 'overdue' ? 'rgba(252,129,74,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${variant === 'overdue' ? 'rgba(252,129,74,0.35)' : 'var(--border-subtle)'}`,
      }}
    >
      <button type="button" onClick={onOpen} className="press w-full text-left p-2.5 flex items-center gap-2 touch-manipulation min-h-[44px]">
        <CalendarClock size={14} color={variant === 'overdue' ? '#FC814A' : '#63B3ED'} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }}>{task.title}</p>
          <p className="text-[10px] truncate" style={{ color: variant === 'overdue' ? '#FC814A' : 'var(--text-3)' }}>
            {sub} · до {dueLabel}
          </p>
        </div>
      </button>
      <div className="flex border-t" style={{ borderColor: 'var(--border-subtle)' }}>
        {isCall && telHref ? (
          <a
            href={telHref}
            onClick={() => {
              hapticSelection();
              void onCall();
            }}
            className="press flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold touch-manipulation min-h-[40px]"
            style={{ color: '#68D391' }}
          >
            <Phone size={12} /> Позвонить
          </a>
        ) : (
          <button
            type="button"
            onClick={isCall ? onCall : onOpen}
            className="press flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold touch-manipulation min-h-[40px]"
            style={{ color: '#63B3ED' }}
          >
            <Phone size={12} /> {isCall ? 'Звонок' : 'Сделка'}
          </button>
        )}
        <button
          type="button"
          onClick={onSnooze}
          className="press flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold touch-manipulation min-h-[40px] border-l"
          style={{ color: 'var(--text-3)', borderColor: 'var(--border-subtle)' }}
        >
          <Clock size={12} /> Завтра
        </button>
      </div>
    </div>
  );
}
